import Modal from "bootstrap/js/dist/modal";
import * as sammelrepository from "./fimportal";
import type { Field, Group, ParentIdentifier } from "./project";
import type { ProjectStore } from "./store";

type SelectElementCallback = (
  event: "new-group" | "new-field" | "link-group" | "link-field",
  elementIdentifier?: [string, string, string],
) => void;

export class GeneralSearchMenu {
  public root: HTMLElement;

  // An increasing counter to identify and skip out-of-order seach-results.
  private searchVersionCounter = 0;

  private localFieldResults: ResultContainer;
  private localGroupResults: ResultContainer;
  private sammelrepoGroupResults: ResultContainer;
  private sammelrepoFieldResults: ResultContainer;

  constructor(
    private store: ProjectStore,
    private onSelect: SelectElementCallback,
    private onNavigate: (
      target: "all-sammelrepo-groups" | "all-sammelrepo-fields",
    ) => void,
    private blockedGroups: Set<string>,
  ) {
    this.root = document.createElement("div");

    this.root.innerHTML = `
        <div class="list-group">
            <a id="create-new-group" href="#" class="list-group-item list-group-item-action">
                <i class="bi bi-plus-circle me-2"></i>
                Neue Gruppe erstellen
            </a>
            <a id="create-new-field" href="#" class="list-group-item list-group-item-action">
                <i class="bi bi-plus-circle me-2"></i>
                Neues Feld erstellen
            </a>
        </div>
        `;

    const addNewGroup = this.root.querySelector("#create-new-group");
    addNewGroup?.addEventListener("click", (event) => {
      event.preventDefault();
      onSelect("new-group");
    });

    const addNewField = this.root.querySelector("#create-new-field");
    addNewField?.addEventListener("click", (event) => {
      event.preventDefault();
      onSelect("new-field");
    });

    this.localGroupResults = new ResultContainer("Lokale Gruppen");
    // TODO: Show again when local elements are supportedq
    // this.root.appendChild(this.localGroupResults.root);

    this.localFieldResults = new ResultContainer("Lokale Felder");
    // TODO: Show again when local elements are supportedq
    // this.root.appendChild(this.localFieldResults.root);

    this.sammelrepoGroupResults = new ResultContainer(
      "Gruppen (Sammelrepository)",
      () => {
        this.onNavigate("all-sammelrepo-groups");
      },
    );
    this.root.appendChild(this.sammelrepoGroupResults.root);

    this.sammelrepoFieldResults = new ResultContainer(
      "Felder (Sammelrepository)",
      () => {
        this.onNavigate("all-sammelrepo-fields");
      },
    );
    this.root.appendChild(this.sammelrepoFieldResults.root);
  }

  public async updateSearchTerm(term: string) {
    this.searchSammelrepo(term);
    this.searchLocalGroups(term);
    this.searchLocalFields(term);
  }

  private searchLocalGroups(term: string) {
    let groups = Object.values(this.store.groups).filter(
      (group) => !this.blockedGroups.has(group.fimId),
    );

    if (term !== "") {
      groups = groups.filter((group) =>
        group.basicData.name.toLowerCase().includes(term),
      );
    }

    const groupCallback = () => {
      alert("Der Import von lokalen Elementen wird aktuell nicht unterstützt.");
    };
    const results = groups.map(
      (group) => new GroupResult(group, groupCallback),
    );

    this.localGroupResults.setResults(results, groups.length);
  }

  private searchLocalFields(term: string) {
    let fields = Object.values(this.store.fields);
    if (term !== "") {
      fields = fields.filter((field) =>
        field.basicData.name.toLowerCase().includes(term),
      );
    }

    const fieldCallback = () => {
      alert("Der Import von lokalen Elementen wird aktuell nicht unterstützt.");
    };
    const results = fields.map(
      (field) => new FieldResult(field, fieldCallback),
    );

    this.localFieldResults.setResults(results, fields.length);
  }

  private async searchSammelrepo(term: string) {
    if (term === "") {
      this.sammelrepoGroupResults.setResults([], 0);
      this.sammelrepoFieldResults.setResults([], 0);
      return;
    }

    const searchVersion = ++this.searchVersionCounter;

    // TODO: Error Handling
    const [groups, fields] = await Promise.all([
      sammelrepository.searchGroups(term, 3),
      sammelrepository.searchFields(term, 3),
    ]);

    // Only show the result if no other query has been started
    if (this.searchVersionCounter === searchVersion) {
      const groupResults = groups.items.map((group) => {
        return new ExternGroupResult(group, () => {
          this.onSelect("link-group", [
            group.namespace,
            group.fim_id,
            group.fim_version,
          ]);
        });
      });

      this.sammelrepoGroupResults.setResults(groupResults, groups.total_count);

      const fieldResult = fields.items.map((field) => {
        return new ExternFieldResult(field, () => {
          this.onSelect("link-field", [
            field.namespace,
            field.fim_id,
            field.fim_version,
          ]);
        });
      });

      this.sammelrepoFieldResults.setResults(fieldResult, fields.total_count);
    }
  }
}

export class SammelrepoFieldsSearchMenu {
  public root: HTMLElement;

  // An increasing counter to identify and skip out-of-order seach-results.
  private searchVersionCounter = 0;

  private sammelrepoResults: ResultContainer;

  constructor(
    private onSelect: SelectElementCallback,
    private onBack: () => void,
  ) {
    this.root = document.createElement("div");

    this.sammelrepoResults = new ResultContainer(
      "Felder (Sammelrepository)",
      undefined,
      this.onBack,
    );
    this.root.appendChild(this.sammelrepoResults.root);
  }

  public async updateSearchTerm(term: string) {
    if (term === "") {
      this.sammelrepoResults.setResults([], 0);
      return;
    }

    const searchVersion = ++this.searchVersionCounter;

    let fields: sammelrepository.SearchResult<sammelrepository.FieldSearchResultItem>;
    try {
      fields = await sammelrepository.searchFields(term, 20);
    } catch (error) {
      // TODO: Error Handling
      console.error(error);
      return;
    }

    // Only show the result if no other query has been started
    if (this.searchVersionCounter === searchVersion) {
      const fieldResults = fields.items.map((field) => {
        return new ExternFieldResult(field, () => {
          this.onSelect("link-field", [
            field.namespace,
            field.fim_id,
            field.fim_version,
          ]);
        });
      });

      this.sammelrepoResults.setResults(fieldResults, fields.total_count);
    }
  }
}

export class SammelrepoGroupsSearchMenu {
  public root: HTMLElement;

  // An increasing counter to identify and skip out-of-order seach-results.
  private searchVersionCounter = 0;

  private sammelrepoGroupResults: ResultContainer;

  constructor(
    private onSelect: SelectElementCallback,
    private onBack: () => void,
  ) {
    this.root = document.createElement("div");

    this.sammelrepoGroupResults = new ResultContainer(
      "Gruppen (Sammelrepository)",
      undefined,
      this.onBack,
    );
    this.root.appendChild(this.sammelrepoGroupResults.root);
  }

  public async updateSearchTerm(term: string) {
    if (term === "") {
      this.sammelrepoGroupResults.setResults([], 0);
      return;
    }

    const searchVersion = ++this.searchVersionCounter;

    let groups: sammelrepository.SearchResult<sammelrepository.GroupSearchResultItem>;
    try {
      groups = await sammelrepository.searchGroups(term, 20);
    } catch (error) {
      // TODO: Error Handling
      console.error(error);
      return;
    }

    // Only show the result if no other query has been started
    if (this.searchVersionCounter === searchVersion) {
      const groupResults = groups.items.map((group) => {
        return new ExternGroupResult(group, () => {
          this.onSelect("link-group", [
            group.namespace,
            group.fim_id,
            group.fim_version,
          ]);
        });
      });

      this.sammelrepoGroupResults.setResults(groupResults, groups.total_count);
    }
  }
}

export class SearchMenu {
  public root: HTMLElement;

  private modal: Modal;

  private input: HTMLInputElement;
  private searchMenuContainer: HTMLDivElement;
  private searchMenu:
    | GeneralSearchMenu
    | SammelrepoGroupsSearchMenu
    | SammelrepoFieldsSearchMenu
    | undefined = undefined;

  private target: ParentIdentifier | undefined = undefined;

  private inputTimer: number | undefined = undefined;

  constructor(private store: ProjectStore) {
    this.root = document.createElement("div");
    this.root.classList.add("modal");
    this.root.setAttribute("aria-hidden", "true");
    this.root.setAttribute("tabindex", "-1");

    this.root.innerHTML = `
            <div class="modal-dialog modal-dialog-centered modal-lg">
                <div class="modal-content">
                    <div class="modal-body">
                        <input id="search-input" class="form-control" type="text" placeholder="Elemente suchen" />
                        <div id="search-menu" class="overflow-y-scroll mt-2" style="height: 50vh;">
                        </div>
                    </div>
                </div>
            </div
        `;

    this.input = this.root.querySelector("#search-input")!;

    this.input.addEventListener("input", (_event) => {
      const term = this.input.value.trim().toLowerCase();

      if (this.inputTimer !== undefined) {
        clearTimeout(this.inputTimer);
      }

      setTimeout(() => {
        if (this.searchMenu !== undefined) {
          this.searchMenu.updateSearchTerm(term);
        }
        this.inputTimer = undefined;
      }, 100);
    });

    this.searchMenuContainer = this.root.querySelector("#search-menu")!;
    this.modal = new Modal(this.root);

    this.root.addEventListener("hidden.bs.modal", () => {
      this.searchMenuContainer.innerHTML = "";
      this.searchMenu = undefined;
    });
  }

  private getTarget(): ParentIdentifier {
    // This should be impossible
    if (this.target === undefined) {
      throw new Error("target is undefined");
    }

    return this.target;
  }

  private async selectItem(
    event: "new-group" | "new-field" | "link-group" | "link-field",
    elementIdentifier?: [string, string, string],
  ) {
    switch (event) {
      case "new-group": {
        this.store.addNewGroup(this.getTarget());
        break;
      }
      case "new-field": {
        this.store.addNewField(this.getTarget());
        break;
      }
      case "link-group": {
        await this.store.importGroupFromFimPortal(
          this.getTarget(),
          elementIdentifier![1],
          elementIdentifier![2],
        );
        break;
      }
      case "link-field": {
        await this.store.importFieldFromFimPortal(
          this.getTarget(),
          elementIdentifier![1],
          elementIdentifier![2],
        );
        break;
      }
    }

    this.modal.hide();
  }

  public open(target: ParentIdentifier) {
    // TODO: block direct children
    // TODO: maybe just disable instead of exclude. Add tooltip with an explanation.
    this.target = target;
    let blockedGroups: Set<string> = new Set();
    if (this.target.type === "group") {
      blockedGroups = this.store.getAllParents(this.target.fimId);
      blockedGroups.add(this.target.fimId);
    }

    this.searchMenu = new GeneralSearchMenu(
      this.store,
      this.selectItem.bind(this),
      this.onNavigate.bind(this),
      blockedGroups,
    );

    this.searchMenuContainer.appendChild(this.searchMenu.root);

    this.input.value = "";
    this.modal.show();
    this.input.focus();
  }

  private onNavigate(
    target:
      | "all-sammelrepo-groups"
      | "all-sammelrepo-fields"
      | "fields-and-groups",
  ) {
    if (target === "all-sammelrepo-groups") {
      this.searchMenu = new SammelrepoGroupsSearchMenu(
        this.selectItem.bind(this),
        () => this.onNavigate("fields-and-groups"),
      );
    } else if (target === "all-sammelrepo-fields") {
      this.searchMenu = new SammelrepoFieldsSearchMenu(
        this.selectItem.bind(this),
        () => this.onNavigate("fields-and-groups"),
      );
    } else {
      this.searchMenu = new GeneralSearchMenu(
        this.store,
        this.selectItem.bind(this),
        this.onNavigate.bind(this),
        new Set(),
      );
    }

    const term = this.input.value.trim().toLowerCase();
    this.searchMenu.updateSearchTerm(term);

    this.searchMenuContainer.innerHTML = "";
    this.searchMenuContainer.appendChild(this.searchMenu.root);
  }
}

class ExternGroupResult {
  readonly root: HTMLElement;

  constructor(
    group: sammelrepository.GroupSearchResultItem,
    onClick: () => void,
  ) {
    this.root = document.createElement("a");
    this.root.setAttribute("href", "#");
    this.root.classList.add("list-group-item", "list-group-item-action");

    this.root.innerHTML = `
        <div class="d-flex flex-column w-100">
            <h6 class="mb-1">${group.name}</h6>
            <small>${group.fim_id} v${group.fim_version}</small>
        </div>
    `;

    this.root.addEventListener("click", (event) => {
      event.preventDefault();
      onClick();
    });
  }
}

class ExternFieldResult {
  readonly root: HTMLElement;

  constructor(
    field: sammelrepository.FieldSearchResultItem,
    onClick: () => void,
  ) {
    this.root = document.createElement("a");
    this.root.setAttribute("href", "#");
    this.root.classList.add("list-group-item", "list-group-item-action");

    this.root.innerHTML = `
        <div class="d-flex flex-column w-100">
            <h6 class="mb-1">${field.name}</h6>
            <small>${field.fim_id} v${field.fim_version}</small>
        </div>
    `;

    this.root.addEventListener("click", (event) => {
      event.preventDefault();
      onClick();
    });
  }
}

class GroupResult {
  readonly root: HTMLElement;

  constructor(group: Group, onClick: () => void) {
    this.root = document.createElement("a");
    this.root.setAttribute("href", "#");
    this.root.classList.add("list-group-item", "list-group-item-action");

    this.root.innerHTML = `
        <div class="d-flex flex-column w-100">
            <h6 class="mb-1">${group.basicData.name}</h6>
            <small>${group.fimId}</small>
        </div>
    `;

    this.root.addEventListener("click", (event) => {
      event.preventDefault();
      onClick();
    });
  }
}

class FieldResult {
  readonly root: HTMLElement;

  constructor(field: Field, onClick: () => void) {
    this.root = document.createElement("a");
    this.root.setAttribute("href", "#");
    this.root.classList.add("list-group-item", "list-group-item-action");

    this.root.innerHTML = `
        <div class="d-flex flex-column w-100">
            <h6 class="mb-1">${field.basicData.name}</h6>
            <small>${field.fimId}</small>
        </div>
    `;

    this.root.addEventListener("click", (event) => {
      event.preventDefault();
      onClick();
    });
  }
}

type Result = GroupResult | FieldResult | ExternFieldResult;

class ResultContainer {
  readonly root: HTMLElement;

  readonly itemCounter: HTMLElement;
  readonly listGroup: HTMLElement;

  constructor(
    title: string,
    private onShowMore?: () => void,
    private onBack?: () => void,
  ) {
    this.root = document.createElement("div");
    this.root.innerHTML = `
        <h6 class="d-flex align-items-center ${
          this.onBack === undefined ? "mt-3" : ""
        } text-primary">
            <a href="#" id="back-button" class="btn btn-outline-primary btn-sm me-2" hidden>
                <i class='bi bi-arrow-left'></i>
            </a>
            ${title} <span></span>
        </h6>
        <div class="list-group">
            <p class="text-muted">Gib mindestens ein Zeichen ein, um im Sammelrepository zu suchen.</p>
        </div>
    `;

    this.itemCounter = this.root.querySelector("span")!;
    this.listGroup = this.root.querySelector(".list-group")!;
    if (this.onBack !== undefined) {
      const onBack = this.onBack;
      const backButton = this.root.querySelector("#back-button")!;
      backButton.removeAttribute("hidden");
      backButton.addEventListener("click", (event) => {
        event.preventDefault();
        onBack();
      });
    }
  }

  public setResults(results: Result[], total_results: number) {
    this.itemCounter.textContent = `(${total_results})`;
    this.listGroup.innerHTML = "";

    if (results.length === 0) {
      this.listGroup.innerHTML =
        "<p class='text-muted'>Keine Ergebnisse gefunden</p>";
      return;
    }

    for (const result of results) {
      this.listGroup.appendChild(result.root);
    }

    if (results.length < total_results && this.onShowMore !== undefined) {
      const onShowMore = this.onShowMore;
      const showAll = document.createElement("a");
      showAll.setAttribute("href", "#");
      showAll.classList.add("list-group-item", "list-group-item-action");
      showAll.innerHTML = "Alle anzeigen";
      showAll.addEventListener("click", (event) => {
        event.preventDefault();
        onShowMore();
      });

      this.listGroup.appendChild(showAll);
    }
  }
}
